import React, { useState, useEffect } from 'react';

interface LoadingComponentProps {
  borderWidth: number;
  containerSize: number;
  message?: string;
  initialSeconds?: number;
}

const LoadingComponentScalable: React.FC<LoadingComponentProps> = ({
  borderWidth,
  containerSize,
  message,
  initialSeconds
}) => {
  const [seconds, setSeconds] = useState<number | undefined>(initialSeconds);
  const [dotCount, setDotCount] = useState(0);

  // --- Dynamic Scaling Calculations ---
  // Timer is roughly 22% of container size
  const timerFontSize = Math.floor(containerSize * 0.22);
  // Message is roughly 15% of container size, but capped for readability
  const messageFontSize = Math.max(Math.floor(containerSize * 0.15), 14);
  // Spacing between spinner and text
  const gapSize = Math.floor(containerSize * 0.2);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      if (seconds !== undefined && seconds > 0) {
        setSeconds((prev) => prev! - 1);
      }
      setDotCount((prev) => (prev + 1) % 4);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [seconds]);

  const calculateInnerSize = (percentage: number) =>
    containerSize - (containerSize * percentage) / 100;

  const showTimer = seconds !== undefined && seconds >= 0;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: `${gapSize}px`, // Scalable gap
    }}>
      {/* SPINNER SECTION */}
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: `${containerSize}px`,
        width: `${containerSize}px`,
      }}>
        {/* Animated Rings */}
        <div style={{ position: 'absolute', border: `${borderWidth}px solid #f3f3f3`, borderBottom: `${borderWidth}px solid #cc0000`, borderRadius: '50%', width: `${calculateInnerSize(20)}px`, height: `${calculateInnerSize(20)}px`, animation: 'spin 1s linear infinite' }}></div>
        <div style={{ position: 'absolute', border: `${borderWidth}px solid #f3f3f3`, borderTop: `${borderWidth}px solid #6699ff`, borderRadius: '50%', width: `${calculateInnerSize(40)}px`, height: `${calculateInnerSize(40)}px`, animation: 'spinReverse 1.2s linear infinite' }}></div>
        <div style={{ position: 'absolute', border: `${borderWidth}px solid #f3f3f3`, borderTop: `${borderWidth}px solid #cc0000`, borderBottom: `${borderWidth}px solid #cc0000`, borderRadius: '50%', width: `${calculateInnerSize(60)}px`, height: `${calculateInnerSize(60)}px`, animation: 'spin 1.4s linear infinite' }}></div>

        {/* STATIC TIMER CENTER */}
        <div style={{
          position: 'absolute',
          width: `${calculateInnerSize(80)}px`,
          height: `${calculateInnerSize(80)}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
        }}>
          {showTimer && (
            <span style={{
              fontSize: `${timerFontSize}px`, // DYNAMIC FONT SIZE
              fontWeight: 'bold',
              background: 'white',
              color: '#333',
              fontFamily: 'sans-serif',
              padding: `${borderWidth}px`, // Padding scales with border
              borderRadius: '50%',
              lineHeight: 1,
            }}>
              {seconds}
            </span>
          )}
        </div>
      </div>

      {/* EXTERNAL MESSAGE SECTION */}
      {message && (
        <div style={{
          fontSize: `${messageFontSize}px`, // DYNAMIC FONT SIZE
          fontWeight: 600,
          color: '#0066cc',
          textAlign: 'center',
          fontFamily: 'sans-serif',
          display: 'flex',
          alignItems: 'baseline'
        }}>
          <span>{message}</span>

          {/* FIXED WIDTH DOTS - Width based on 'em' so it scales with font-size */}
          <span style={{
            textAlign: 'left',
            display: 'inline-block',
            width: '1.5em',
            marginLeft: '2px'
          }}>
            {'.'.repeat(dotCount)}
          </span>
        </div>
      )}

      <style>
        {`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes spinReverse { 0% { transform: rotate(0deg); } 100% { transform: rotate(-360deg); } }
        `}
      </style>
    </div>
  );
};

export default LoadingComponentScalable;
